/**
 * InstallPrompt — discreet bottom banner that surfaces the PWA install flow.
 *
 * - On supported browsers (Chrome, Edge, Brave, Samsung Internet, Firefox
 *   Android, Opera), the `beforeinstallprompt` event fires automatically
 *   when the install criteria are met. We capture it and expose a small
 *   "Installer l'app" button.
 * - On iOS Safari, that event doesn't exist, so we detect iOS and show a
 *   one-time hint with the manual "Share → Add to Home Screen" gesture.
 * - The banner is auto-dismissed for 30 days after the user clicks
 *   "Plus tard" so we don't nag.
 */

import { useEffect, useState } from 'react';
import { Smartphone, X, Download } from 'lucide-react';

const DISMISS_KEY = 'dm-pwa-install-dismissed-until';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Heuristics to decide whether to show the banner at all.
function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isDismissed() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until && Date.now() < until;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    // Android / Chromium browsers — capture the install prompt for later.
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setHidden(false);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — show manual hint after a short delay (avoids competing
    // with the cookie banner on first paint).
    if (isIos()) {
      const t = setTimeout(() => {
        setShowIosHint(true);
        setHidden(false);
      }, 8000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS));
    } catch {
      // localStorage might be blocked in private mode — fail silently
    }
    setHidden(true);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      setHidden(true);
    }
  };

  if (hidden) return null;

  // Render variant: iOS manual hint vs Android/Desktop one-click.
  const isIosVariant = !deferredPrompt && showIosHint;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-[1000] rounded-xl bg-slate-900 text-slate-100 shadow-2xl border border-slate-800 p-4"
      role="dialog"
      aria-label="Installer Drone Maps"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-sky-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Installer Drone Maps</div>
          {isIosVariant ? (
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Sur iPhone&nbsp;: appuyez sur <span className="font-semibold text-slate-200">Partager</span> puis sur <span className="font-semibold text-slate-200">« Sur l’écran d’accueil »</span> pour utiliser Drone Maps comme une vraie app, même hors-ligne.
            </p>
          ) : (
            <p className="text-xs text-slate-400 mt-1">
              Accédez à Drone Maps en un tap, plein écran, et utilisez vos cartes ED-269 même sans réseau.
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {!isIosVariant && (
              <button
                onClick={install}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500 hover:bg-sky-400 text-slate-950 text-xs font-semibold"
                data-testid="pwa-install-btn"
              >
                <Download className="w-3.5 h-3.5" /> Installer
              </button>
            )}
            <button
              onClick={dismiss}
              className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1"
              data-testid="pwa-install-dismiss"
            >
              {isIosVariant ? 'J’ai compris' : 'Plus tard'}
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-slate-500 hover:text-slate-300 shrink-0"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
